import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';

import { Button, Icon } from '~components';
import { ENABLE_NEW_TV_GUIDE } from '~source/core/constants';
import useAuth from '~hooks/use-auth';
import { useSettings } from '~hooks/use-settings';
import { usePageDimensions } from '~hooks/use-page-dim';
import { useOutside } from '~source/ui/hooks/use-outside';

import $ from './header.scss';

interface Route {
    name: string;
    path: string;
    condition?: boolean;
}

export function Header(): JSX.Element {
    const history = useHistory();
    const [pageWidth] = usePageDimensions();
    const { isAuthorized, logout } = useAuth();
    const settings = useSettings();

    const [popoverOpen, setPopoverOpen] = useState(false);
    // Keep pageWidth dependency so resize-driven effects still update if needed.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const pageWidthForEffects = pageWidth;

    const routes = useMemo(
        () =>
            [
                {
                    name: 'Home',
                    path: '/',
                    condition: true,
                },
                {
                    name: 'Kijk live',
                    path: '/live',
                    condition: settings?.hasSubscription,
                },
                ENABLE_NEW_TV_GUIDE
                    ? {
                          name: 'TV Gids',
                          path: '/tv-gids',
                          condition: true,
                      }
                    : null,
                {
                    name: 'Wedstrijden/Events',
                    path: '/payperview',
                    condition: true,
                },
            ].filter((route) => route && route.condition) as Route[],
        [settings],
    );

    const onAccount = useCallback(() => history.push('/account'), [history]);
    const onLogin = useCallback(() => history.push('/login'), [history]);
    const onLogout = useCallback(() => logout(), [logout]);

    useEffect(() => {
        function bodyScrollLockListener() {
            if (window.outerWidth > 864) {
                clearAllBodyScrollLocks();
                setPopoverOpen(false);
            }
        }
        window.addEventListener('resize', bodyScrollLockListener);
        return () => {
            window.removeEventListener('resize', bodyScrollLockListener);
            clearAllBodyScrollLocks();
        };
    }, [setPopoverOpen]);

    const toggleDropdown = useCallback(
        (state?: boolean) => {
            const newState = state ?? !popoverOpen;
            if (newState === true) disableBodyScroll(document.body);
            else clearAllBodyScrollLocks();
            setPopoverOpen(newState);
        },
        [popoverOpen, setPopoverOpen],
    );

    const route2Match = useRouteMatch(routes[1].path);
    const activeRoute: boolean[] = [
        useRouteMatch(routes[0].path)?.isExact ?? false,
        routes[1].condition! ? !!route2Match : (route2Match?.isExact ?? false),
        !!useRouteMatch(routes[2].path),
    ];

    const popover = useRef<HTMLDivElement>(null);
    useOutside(popover, () => popoverOpen && toggleDropdown(false));

    return (
        <header className={$.header}>
            <div className={$.headerTop}>
                <div>
                    <div>
                        <div className={$.headerLogo}>
                            <Link to='/' aria-label='Home'>
                                <Icon type='ziggoSportTotaal' size={32} />
                            </Link>
                        </div>
                        <div className={$.headerButtons}>
                            {isAuthorized ? (
                                <>
                                    <Button
                                        color='secondary'
                                        onClick={onAccount}
                                        className={$.desktop}
                                        label={<p>Mijn Ziggo Sport</p>}
                                    />
                                    <Button
                                        color='primary'
                                        onClick={onLogout}
                                        className={$.desktop}
                                        label={
                                            <>
                                                <Icon
                                                    type='user'
                                                    size={20}
                                                    className={$.userFix}
                                                    color='black'
                                                />
                                                <span>Uitloggen</span>
                                            </>
                                        }
                                    />
                                    <div
                                        ref={popover}
                                        className={$.popover}
                                        data-open={popoverOpen}
                                    >
                                        {/* eslint-disable-next-line */}
                                        <p
                                            onClick={() => {
                                                toggleDropdown(false);
                                                onAccount();
                                            }}
                                        >
                                            Mijn Ziggo Sport
                                        </p>
                                        {/* eslint-disable-next-line */}
                                        <p
                                            onClick={() => {
                                                toggleDropdown(false);
                                                onLogout();
                                            }}
                                        >
                                            Uitloggen
                                        </p>
                                    </div>
                                    <Button
                                        color='secondary'
                                        onClick={() => toggleDropdown()}
                                        className={$.mobile}
                                        label={
                                            <>
                                                <Icon type='menu' size={20} color='black' />
                                                <p>Menu</p>
                                            </>
                                        }
                                    />
                                </>
                            ) : (
                                <>
                                    <Button
                                        color='secondary'
                                        onClick={onLogin}
                                        label={
                                            <>
                                                <Icon
                                                    type='user'
                                                    width={15.98}
                                                    height={17.92}
                                                    color='black'
                                                />
                                                <span>Login</span>
                                            </>
                                        }
                                    />
                                    <a
                                        target='_blank'
                                        rel='noreferrer'
                                        href='https://www.ziggosport.nl/neem-ziggo-sport/'
                                    >
                                        <Button
                                            color='primary'
                                            label={
                                                <span>
                                                    Neem Ziggo Sport
                                                    <Icon type='externalLink' size={16.25} />
                                                </span>
                                            }
                                        />
                                    </a>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className={$.navigation}>
                <div>
                    <div className={$.navigationLogo}>
                        <Icon type='ziggoSportTotaal' size={32} color='black' />
                    </div>
                    {routes.map((route, i) => (
                        <Link
                            key={route.path}
                            to={route.path}
                            data-active={activeRoute[i]}
                            className={$.navigationLink}
                        >
                            <p>{route.name}</p>
                            <div className={$.active} data-active={activeRoute[i]} />
                        </Link>
                    ))}
                </div>
            </div>
        </header>
    );
}
